import { useMemo, useRef, useEffect, useState } from 'react';
import type { WidgetProps } from '@rjsf/utils';

declare global {
  interface Window {
    Calendly?: {
      initInlineWidget: (opts: {
        url: string;
        parentElement: HTMLElement;
        prefill?: { name?: string; email?: string };
        resize?: boolean;
      }) => void;
    };
  }
}

interface IframeWidgetProps extends WidgetProps {
  formContext?: {
    formData?: Record<string, unknown>;
  };
}

/** Detect whether a URL points to Calendly. */
function isCalendlyUrl(src: string): boolean {
  try {
    const { hostname } = new URL(src);
    return hostname.endsWith('calendly.com');
  } catch {
    return false;
  }
}

/** Load the Calendly external widget script once. */
let calendlyScriptPromise: Promise<void> | null = null;
function loadCalendlyScript(): Promise<void> {
  if (calendlyScriptPromise) return calendlyScriptPromise;
  calendlyScriptPromise = new Promise<void>((resolve, reject) => {
    if (window.Calendly) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Calendly widget script'));
    document.head.appendChild(script);
  });
  return calendlyScriptPromise;
}

const CALENDLY_TRUSTED_ORIGINS = ['https://calendly.com', 'https://assets.calendly.com', 'https://calendly-embed.com'];

const isTrustedCalendlyOrigin = (origin: string): boolean => {
  return CALENDLY_TRUSTED_ORIGINS.some((trustedOrigin) => origin.startsWith(trustedOrigin));
};

const getCalendlyPageHeight = (data: unknown): number | null => {
  if (typeof data !== 'object' || data === null) return null;

  const payload = data as Record<string, unknown>;
  const eventType = payload.event;
  if (typeof eventType !== 'string' || !eventType.startsWith('calendly.page_height')) {
    return null;
  }

  const eventPayload = payload.payload;
  if (typeof eventPayload !== 'object' || eventPayload === null) return null;

  const rawHeight = (eventPayload as Record<string, unknown>).height;
  const height = typeof rawHeight === 'number' ? rawHeight : Number(rawHeight);
  if (!Number.isFinite(height) || height <= 0) return null;

  return Math.ceil(height);
};

/** Resolve prefill values from formData, using ui:options overrides or common field names. */
function usePrefillValues(options: WidgetProps['options'], formData: Record<string, unknown>) {
  const email = String(
    (options?.emailField ? formData[options.emailField as string] : null) ||
      formData.email ||
      formData.work_email ||
      formData.workEmail ||
      '',
  );

  const firstName = String(
    (options?.firstNameField ? formData[options.firstNameField as string] : null) ||
      formData.firstName ||
      formData.first_name ||
      '',
  );

  const lastName = String(
    (options?.lastNameField ? formData[options.lastNameField as string] : null) ||
      formData.lastName ||
      formData.last_name ||
      '',
  );

  const fullName = String(
    (options?.nameField ? formData[options.nameField as string] : null) ||
      formData.name ||
      formData.full_name ||
      formData.fullName ||
      '',
  );

  const name = [firstName, lastName].filter(Boolean).join(' ') || fullName;

  return { email, name };
}

/** Calendly inline widget using the official JS API with prefill support. */
function CalendlyEmbed({ src, height, email, name }: { src: string; height: number; email: string; name: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const prevKeyRef = useRef<string>('');
  const [embedHeight, setEmbedHeight] = useState<number>(height);

  useEffect(() => {
    setEmbedHeight(height);
  }, [height, src]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent): void => {
      if (!isTrustedCalendlyOrigin(event.origin)) return;

      const nextHeight = getCalendlyPageHeight(event.data);
      if (!nextHeight) return;

      setEmbedHeight((currentHeight) => {
        const resolvedHeight = Math.max(nextHeight, height);
        return currentHeight === resolvedHeight ? currentHeight : resolvedHeight;
      });
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [height]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // Build a key from the inputs so we only re-init when something meaningful changes.
    const key = `${src}|${email}|${name}`;
    if (key === prevKeyRef.current) return;

    let cancelled = false;

    loadCalendlyScript()
      .then(() => {
        if (cancelled || !window.Calendly || !containerRef.current) return;
        prevKeyRef.current = key;

        // Clear any previous widget instance
        containerRef.current.innerHTML = '';

        const prefill: { name?: string; email?: string } = {};
        if (email) prefill.email = email;
        if (name) prefill.name = name;

        window.Calendly.initInlineWidget({
          url: src,
          parentElement: containerRef.current,
          prefill,
          resize: true,
        });
      })
      .catch((err) => {
        console.error('Calendly widget failed to load:', err);
      });

    return () => {
      cancelled = true;
    };
  }, [src, email, name]);

  return (
    <div
      ref={containerRef}
      className="w-full rounded overflow-hidden"
      style={{ minHeight: height, height: embedHeight }}
    />
  );
}

/** Plain iframe embed for non-Calendly URLs with URL-param prefill. */
function PlainIframe({
  src,
  height,
  email,
  name,
  label,
}: {
  src: string;
  height: number;
  email: string;
  name: string;
  label?: string;
}) {
  const finalSrc = useMemo(() => {
    const url = new URL(src);
    if (email) url.searchParams.set('email', email);
    if (name) url.searchParams.set('name', name);
    return url.toString();
  }, [src, email, name]);

  return (
    <iframe
      src={finalSrc}
      width="100%"
      height={height}
      frameBorder="0"
      className="rounded"
      title={label || 'Embedded Content'}
    />
  );
}

// Custom Iframe Widget for Calendly and other embeds
// Supports prefilling Calendly with name and email from form data.
// Field names are configurable via ui:options (emailField, firstNameField, lastNameField, nameField).
export const IframeWidget = (props: IframeWidgetProps) => {
  const { options } = props;
  const baseSrc = (options?.src as string) || '';
  const height = (options?.height as number) || 700;

  // RJSF v6 passes formContext through the registry, not as a direct prop
  const formContext =
    props.formContext ??
    ((props.registry as unknown as Record<string, unknown>)?.formContext as IframeWidgetProps['formContext']);
  const formData = formContext?.formData || {};
  const { email, name } = usePrefillValues(options, formData);

  if (!baseSrc) {
    return <div className="text-muted-foreground text-sm">No iframe source configured</div>;
  }

  return (
    <div className="w-full">
      {isCalendlyUrl(baseSrc) ? (
        <CalendlyEmbed src={baseSrc} height={height} email={email} name={name} />
      ) : (
        <PlainIframe src={baseSrc} height={height} email={email} name={name} label={props.label} />
      )}
    </div>
  );
};
